import { useEffect, useRef, useState } from 'react';
import { Expand, ImageOff } from 'lucide-react';
import type { DocumentResult, LineItemRow } from '../shared/desktop-api';
import type { LineDraftContext } from './line-item-drafts';
import { sourcePageIdentity } from './field-source-geometry';
import {
  sourcePreviewCache,
  watchSourcePreview,
  type SourcePreview,
} from './field-source-preview-cache';
import { resolveRowSource, rowSourceCropBounds } from './row-source-geometry';
import './row-source-crop.css';

/** Read-only companion to the existing editable row, never an editor replacement. */
export function RowSourceCrop({
  document,
  row,
  documentMutationToken,
  tableIdentity,
  context,
  onShowPage,
}: {
  document: DocumentResult;
  row: LineItemRow;
  documentMutationToken: string;
  tableIdentity: string;
  context?: LineDraftContext;
  onShowPage?: (pageNumber: number) => void;
}) {
  const element = useRef<HTMLDivElement>(null);
  const [visibleIdentity, setVisibleIdentity] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{
    identity: string;
    preview?: SourcePreview;
    failed?: true;
  }>();
  const evidence = resolveRowSource(document, row, documentMutationToken, tableIdentity, context);
  const page = evidence.kind === 'source' ? evidence.page : undefined;
  // Validate row ownership against the current mutation above, but cache pixels
  // by their source page, not by confirmation state. A checkmark changes the
  // document revision, not the paper; clearing every crop collapses the list.
  const identity = page ? sourcePageIdentity(document.id, JSON.stringify(page), page) : '';
  const shared = evidence.kind === 'source' && evidence.scope === 'shared';
  const documentPage = page ? document.pages.indexOf(page) + 1 : undefined;
  useEffect(() => {
    if (!element.current || !identity || typeof IntersectionObserver === 'undefined') return;
    let current = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (current)
          setVisibleIdentity(
            entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)
              ? identity
              : null,
          );
      },
      { threshold: 0 },
    );
    observer.observe(element.current);
    return () => {
      current = false;
      observer.disconnect();
    };
  }, [identity]);
  useEffect(() => {
    setLoaded(undefined);
    if (visibleIdentity !== identity || !page || !identity) return;
    return watchSourcePreview(
      sourcePreviewCache,
      identity,
      page.previewPath,
      (preview) => setLoaded({ identity, preview }),
      () => setLoaded({ identity, failed: true }),
    );
  }, [visibleIdentity, identity]);
  const result = visibleIdentity === identity && loaded?.identity === identity ? loaded : undefined;
  const crop =
    evidence.kind === 'source' && result?.preview
      ? rowSourceCropBounds(evidence, result.preview.width, result.preview.height)
      : null;
  const state =
    evidence.kind === 'missing'
      ? 'missing'
      : result?.failed || (result?.preview && !crop)
        ? 'unavailable'
        : crop
          ? 'ready'
          : 'loading';
  const show = page && onShowPage ? () => onShowPage(page.number) : undefined;
  const content =
    crop && result?.preview ? (
      <>
        <svg
          role="img"
          aria-label={
            shared
              ? `Original shared source area for row ${row.rowNumber}`
              : `Original page area cited for row ${row.rowNumber}`
          }
          viewBox={`${crop.x} ${crop.y} ${crop.width} ${crop.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ aspectRatio: `${crop.width} / ${crop.height}` }}
        >
          <image
            href={result.preview.source}
            x={0}
            y={0}
            width={result.preview.width}
            height={result.preview.height}
          />
        </svg>
        <span className="row-source-caption">
          {shared ? 'Shared area' : 'Row area'} · page {documentPage}
          {show && <Expand size={12} />}
        </span>
      </>
    ) : (
      <span className="row-source-placeholder">
        <ImageOff size={15} />
        <span>
          {evidence.kind === 'missing'
            ? evidence.reason
            : state === 'unavailable'
              ? 'Source preview unavailable'
              : 'Row source preview'}
          {show && <small>Open full page</small>}
        </span>
      </span>
    );
  return (
    <div
      ref={element}
      className="row-source-crop"
      data-source-state={state}
      data-source-row={row.rowNumber}
      data-source-scope={evidence.kind === 'source' ? evidence.scope : undefined}
      title={shared ? 'Shares printed text with another row' : undefined}
      data-source-page-number={page?.number}
      data-source-document-page={documentPage}
    >
      {show ? (
        <button
          type="button"
          aria-label={
            shared
              ? `Show shared source area for row ${row.rowNumber} on page ${documentPage}`
              : `Show row ${row.rowNumber} source on page ${documentPage}`
          }
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            show();
          }}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}
